import { createAsyncThunk } from '@reduxjs/toolkit';
import { getErrorData, type IErrorData } from '../../../utils/getErrorData';
import { type IOrder } from '../../../types/IOrder';
import { type IFetchTrackingHistoryRoot } from '../../../types/IFetchDeliveryStatus';
import { type Ticket } from '../../../api/ticketsApi';
import { getOrderById } from '../../../hooks/orderApi';
import { getOrderTrackingHistory } from '../../../api/deliveryApi';
import { getOrderTickets } from '../../../api/ticketsApi';
import { api } from '../../../utils/api';
import { addOfferToOrder, updateOrder } from '../../../api/orderApi';
import { type IOrderUpdateRequest } from '../../../types/IOrderUpdateRequest';
import { type IOrderAddOfferRequest } from '../../../types/IOrderAddOffer';
import { triggerShipmentFormError } from '../../../utils/shipmentFormEvents';
import { getApiEnvelopeError } from '../../../utils/getApiEnvelopeError';
import { setOrderOffers } from '../index';

// Загрузка заказа по UUID
export const fetchOrderByIdThunk = createAsyncThunk<
  IOrder,
  { uuid: string; signal?: AbortSignal },
  { rejectValue: IErrorData }
>(
  'order/fetchOrderByIdThunk',
  async ({ uuid, signal }, { rejectWithValue }) => {
    try {
      const order = await getOrderById(uuid, signal);
      return order;
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Загрузка полной истории статусов доставки
export const fetchOrderTrackingHistoryThunk = createAsyncThunk<
  IFetchTrackingHistoryRoot | 'Abort' | undefined,
  { orderUuid: string; signal?: AbortSignal },
  { rejectValue: IErrorData }
>(
  'order/fetchOrderTrackingHistoryThunk',
  async ({ orderUuid, signal }, { rejectWithValue }) => {
    try {
      const trackingHistory = await getOrderTrackingHistory(orderUuid, signal);
      return trackingHistory;
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Загрузка тикетов заказа
export const fetchOrderTicketsThunk = createAsyncThunk<
  Ticket[],
  { orderUuid: string; signal?: AbortSignal },
  { rejectValue: IErrorData }
>(
  'order/fetchOrderTicketsThunk',
  async ({ orderUuid, signal }, { rejectWithValue }) => {
    try {
      const data = await getOrderTickets(orderUuid, signal);
      if (data.response !== null) {
        return data.response.items;
      }
      return [];
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Полное обновление данных заказа с новым API (заказ + история статусов доставки + тикеты)
export const refreshOrderDataV2Thunk = createAsyncThunk<
  {
    order: IOrder;
    trackingHistory: IFetchTrackingHistoryRoot | 'Abort' | undefined;
    tickets: Ticket[];
  },
  { uuid: string; signal?: AbortSignal },
  { rejectValue: IErrorData }
>(
  'order/refreshOrderDataV2Thunk',
  async ({ uuid, signal }, { rejectWithValue }) => {
    try {
      const order = await getOrderById(uuid, signal);

      const [trackingHistoryResult, ticketsResult] = await Promise.allSettled([
        getOrderTrackingHistory(order.uuid, signal),
        getOrderTickets(order.uuid, signal)
      ]);

      const trackingHistory =
        trackingHistoryResult.status === 'fulfilled'
          ? trackingHistoryResult.value
          : undefined;

      const tickets =
        ticketsResult.status === 'fulfilled' &&
        ticketsResult.value.response !== null
          ? ticketsResult.value.response.items
          : [];

      return {
        order,
        trackingHistory,
        tickets
      };
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Смена статуса заказа
export const changeOrderStatusThunk = createAsyncThunk<
  { action: string; uuid: string },
  { uuid: string; action: string; forceSet?: boolean },
  { rejectValue: IErrorData }
>(
  'order/changeOrderStatusThunk',
  async ({ uuid, action, forceSet }, { rejectWithValue }) => {
    try {
      let request;
      let url = '/admin/order/' + uuid + '/' + action;

      if (action === 'cancel') {
        request = api.delete;
      } else if (action === 'packing') {
        request = api.put;
        if (forceSet === true) {
          url += '?force_set=true';
        }
      } else {
        request = api.post;
      }

      const response = await request(url);
      if (response.status !== 200) {
        const errorData: IErrorData = getErrorData(response);
        return rejectWithValue(errorData);
      }
      return { action, uuid };
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Комбинированная санка: смена статуса + полное обновление данных заказа без промежуточного сброса pendingAction
export const changeOrderStatusAndRefreshThunk = createAsyncThunk<
  { action: string; uuid: string },
  { uuid: string; action: string; forceSet?: boolean },
  { rejectValue: IErrorData }
>(
  'order/changeOrderStatusAndRefreshThunk',
  async ({ uuid, action, forceSet }, { rejectWithValue, dispatch }) => {
    try {
      let request;
      let url = '/admin/order/' + uuid + '/' + action;

      if (action === 'cancel') {
        request = api.delete;
      } else if (action === 'packing') {
        request = api.put;
        if (forceSet === true) {
          url += '?force_set=true';
        }
      } else {
        request = api.post;
      }

      const response = await request(url);
      if (response.status !== 200) {
        const errorData: IErrorData = getErrorData(response);

        if (errorData.code === 5) {
          triggerShipmentFormError();
        }

        return rejectWithValue(errorData);
      }
      try {
        await dispatch(refreshOrderDataV2Thunk({ uuid })).unwrap();
      } catch (refreshErr) {
        // eslint-disable-next-line no-console
        console.error(
          'Ошибка при обновлении данных заказа после смены статуса:',
          refreshErr
        );
      }
      return { action, uuid };
    } catch (error) {
      const errorData = getErrorData(error);

      if (errorData.code === 5) {
        triggerShipmentFormError();
      }

      return rejectWithValue(errorData);
    }
  }
);

export const updateOrderThunk = createAsyncThunk<
  IOrder,
  { uuid: string; payload: IOrderUpdateRequest },
  { rejectValue: IErrorData }
>('order/updateOrderThunk', async ({ uuid, payload }, { rejectWithValue }) => {
  try {
    const response = await updateOrder(uuid, payload);

    if ('message' in response) {
      return rejectWithValue(response);
    }

    if (response.response === null) {
      return rejectWithValue({
        message: 'Не удалось обновить заказ',
        code: 500
      });
    }

    return response.response;
  } catch (error) {
    const errorData = getErrorData(error);
    return rejectWithValue(errorData);
  }
});

// Привязка заказа к аккаунту покупателя (PUT .../update, поле buyer)
export const linkOrderBuyerThunk = createAsyncThunk<
  IOrder,
  { uuid: string; buyerUuid: string },
  { rejectValue: IErrorData }
>(
  'order/linkOrderBuyerThunk',
  async ({ uuid, buyerUuid }, { rejectWithValue }) => {
    try {
      const response = await updateOrder(uuid, { buyer: buyerUuid });

      if ('message' in response) {
        return rejectWithValue(response);
      }

      if (response.response === null) {
        return rejectWithValue({
          message: 'Не удалось привязать заказ к пользователю',
          code: 500
        });
      }

      return response.response;
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Добавление торгового предложения в заказ. Ответ отдаёт новый состав заказа —
// им сразу обновляется таблица, следом идёт перезалив карточки: вместе с составом
// на бэкенде пересчитываются производные (бонусы, промокод, суммы платежей).
export const addOfferToOrderThunk = createAsyncThunk<
  { uuid: string; offerId: number },
  { uuid: string; payload: IOrderAddOfferRequest },
  { rejectValue: IErrorData }
>(
  'order/addOfferToOrderThunk',
  async ({ uuid, payload }, { rejectWithValue, dispatch }) => {
    try {
      const response = await addOfferToOrder(uuid, payload);
      const envelopeError = getApiEnvelopeError(response.meta);

      if (envelopeError !== null) {
        return rejectWithValue(envelopeError);
      }

      if (response.response !== null) {
        dispatch(setOrderOffers(response.response));
      }

      await dispatch(refreshOrderDataV2Thunk({ uuid })).unwrap();

      return { uuid, offerId: payload.id };
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Обновление конечной цены (продажи) отдельного торгового предложения в заказе
export const updateOrderOfferPriceThunk = createAsyncThunk<
  IOrder,
  { uuid: string; orderOfferId: number; salePrice: number },
  { rejectValue: IErrorData }
>(
  'order/updateOrderOfferPriceThunk',
  async ({ uuid, orderOfferId, salePrice }, { rejectWithValue }) => {
    try {
      const response = await updateOrder(uuid, {
        order_offers: [
          {
            order_offer_id: orderOfferId,
            sale_price: salePrice
          }
        ]
      });

      if ('message' in response) {
        return rejectWithValue(response);
      }

      if (response.response === null) {
        return rejectWithValue({
          message: 'Не удалось обновить цену товара',
          code: 500
        });
      }

      return response.response;
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);
